import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Badge } from '../components/ui/badge';
import { Calendar, ArrowRight, ArrowLeft, Newspaper } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const localeMap = {
  ja: 'ja-JP', en: 'en-US', pt: 'pt-BR', id: 'id-ID',
  vi: 'vi-VN', es: 'es-ES', tl: 'fil-PH', zh: 'zh-CN',
};

function pick(dictLike, language) {
  if (!dictLike) return '';
  if (typeof dictLike === 'string') return dictLike;
  return dictLike[language] || dictLike.ja || Object.values(dictLike)[0] || '';
}

function CategoryBadge({ category, t }) {
  const isSkill = category === 'skill';
  return (
    <Badge className={isSkill ? 'bg-[#00A651] text-white' : 'bg-gray-200 text-gray-700'}>
      {isSkill ? t('courses.skillTraining') : t('newsPage.announcement')}
    </Badge>
  );
}

export default function News() {
  const { t, language } = useLanguage();
  const { id } = useParams();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString(localeMap[language] || 'ja-JP', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  return id
    ? <NewsDetail id={id} t={t} language={language} formatDate={formatDate} />
    : <NewsList t={t} language={language} formatDate={formatDate} />;
}

function NewsList({ t, language, formatDate }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/news`);
        setNews(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setNews([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="news-page">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('news.title')}</h1>
          <p className="text-gray-600 max-w-2xl">{t('newsPage.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12"><p className="text-gray-500">{t('common.loading')}</p></div>
        ) : news.length === 0 ? (
          <div className="text-center py-20" data-testid="news-empty">
            <Newspaper className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">{t('newsPage.empty')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {news.map((item) => (
              <article
                key={item.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-[#00A651] transition-all group"
                data-testid={`news-item-${item.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <CategoryBadge category={item.category} t={t} />
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        <span className="font-[Barlow_Condensed]">{formatDate(item.date)}</span>
                      </div>
                    </div>
                    <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2 group-hover:text-[#00A651] transition-colors">
                      {pick(item.title, language)}
                    </h2>
                    <p className="text-gray-600 line-clamp-2">{pick(item.excerpt, language)}</p>
                  </div>
                  <Link
                    to={`/news/${item.id}`}
                    className="flex items-center gap-1 text-[#00A651] font-semibold hover:underline whitespace-nowrap"
                    data-testid={`news-readmore-${item.id}`}
                  >
                    {t('news.readMore')}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NewsDetail({ id, t, language, formatDate }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/news/${id}`);
        setItem(res.data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const content = item ? pick(item.content, language) || pick(item.excerpt, language) : '';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="news-detail-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/news" className="inline-flex items-center gap-1 text-[#00A651] font-semibold hover:underline mb-6" data-testid="news-back-link">
          <ArrowLeft className="h-4 w-4" />
          {t('newsPage.backToList')}
        </Link>

        {loading ? (
          <div className="text-center py-12"><p className="text-gray-500">{t('common.loading')}</p></div>
        ) : notFound || !item ? (
          <div className="text-center py-20" data-testid="news-notfound">
            <p className="text-gray-500">{t('newsPage.notFound')}</p>
          </div>
        ) : (
          <article className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-10" data-testid="news-article">
            <div className="flex items-center gap-3 mb-4">
              <CategoryBadge category={item.category} t={t} />
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                <span className="font-[Barlow_Condensed]">{formatDate(item.date)}</span>
              </div>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-testid="news-detail-title">
              {pick(item.title, language)}
            </h1>
            <div className="prose max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap" data-testid="news-detail-content">
              {content}
            </div>
          </article>
        )}
      </div>
    </div>
  );
}
